/**
 * O que um cenário mudou em relação à base dele — em frases.
 *
 * A sugestão do motor (P5) só vale se for legível: *"troca o professor da
 * turma A pra turma C"* (`PESQUISA-ESPM.md` §24.3). Uma grade inteira nova
 * não é sugestão; a diferença é. Serve também para qualquer rodada (P3).
 *
 * **O custo vem antes do ganho.** Se a sugestão encaixou uma aula tirando
 * outra do lugar, a aula que ficou sem lugar aparece primeiro — é a parte que
 * a pessoa precisa decidir se aceita.
 */

import { useEffect, useState } from 'react'

import { ErroApi, sugestao as api, type DiferencaDaBase } from '../api'

const DIA = ['', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado', 'domingo']
const hora = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
const quando = (x: { dia: number; inicio: number }) => `${DIA[x.dia]} ${hora(x.inicio)}`

export function Diferencas({ projeto, cenario }: { projeto: string; cenario: string }) {
  const [d, setD] = useState<DiferencaDaBase | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  // Enquanto o motor trabalha, pergunta de novo — a sugestão leva segundos.
  useEffect(() => {
    let vivo = true
    let espera: ReturnType<typeof setTimeout> | undefined
    const ler = () =>
      api
        .diferencas(projeto, cenario)
        .then((r) => {
          if (!vivo) return
          setD(r)
          setErro(null)
          if (r.estado === 'PROCESSANDO') espera = setTimeout(ler, 2500)
        })
        .catch((e) => vivo && setErro(e instanceof ErroApi ? e.message : 'não foi possível comparar'))
    void ler()
    return () => {
      vivo = false
      if (espera) clearTimeout(espera)
    }
  }, [projeto, cenario])

  if (erro) return <p className="field__hint">{erro}</p>
  if (!d) return <p className="field__hint">Comparando…</p>
  if (d.estado === 'PROCESSANDO') return <p className="field__hint">O motor está trabalhando nisto…</p>
  if (d.estado === 'SEM_SAIDA') {
    return (
      <p className="callout">
        <strong>Não há mudança que resolva.</strong> O motor provou que esta aula não cabe mexendo só na
        grade — é caso de negociar: com o professor, ou com a coordenação, para mudar quem a dá.
      </p>
    )
  }

  const custos = d.mudancas.filter((m) => m.saiu.length > m.entrou.length)
  const resto = d.mudancas.filter((m) => !(m.saiu.length > m.entrou.length))

  return (
    <div className="stack diferencas">
      <p>
        {d.encaixadas > 0 && <strong>{d.encaixadas} aula(s) encaixada(s). </strong>}
        {d.mudancas.length === 0
          ? 'Nada mudou em relação à base.'
          : `${d.mudancas.length} disciplina(s) mudaram de lugar ou de professor.`}
      </p>
      {d.desencaixadas > 0 && (
        <p className="state state--pendencia" role="status">
          Para isso, {d.desencaixadas} aula(s) que estavam na grade ficaram sem lugar — veja abaixo, e
          decida se a troca vale.
        </p>
      )}
      <ul className="diferencas__lista">
        {[...custos, ...resto].map((m) => (
          <li key={m.oferta_id}>
            <strong>
              {m.disciplina} da {m.turma}
            </strong>
            {m.saiu.length > 0 && <> saiu de {m.saiu.map(quando).join(', ')}</>}
            {m.saiu.length > 0 && m.entrou.length > 0 && ' e'}
            {m.entrou.length > 0 && <> foi para {m.entrou.map(quando).join(', ')}</>}
            {m.saiu.length > m.entrou.length && m.entrou.length === 0 && <> — e ficou sem lugar</>}
            {m.professores_antes.join('|') !== m.professores_depois.join('|') &&
              m.professores_antes.length > 0 &&
              m.professores_depois.length > 0 && (
                <>
                  {' '}
                  · professor: {m.professores_antes.join(', ')} → {m.professores_depois.join(', ')}
                </>
              )}
            .
          </li>
        ))}
      </ul>
    </div>
  )
}
